import { useEffect, useState } from "react";
import { api, API, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Mail, FileDown } from "lucide-react";

export function OrderConfirmationDialog({ open, onOpenChange, order, onSent }) {
  const { t, user } = useApp();
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState("custom");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !order) return;
    setSubject(`${t("ord_confirmation")} ${order.order_number} – Encasa`);
    setMessage(t("ord_ab_msg").replace("{num}", order.order_number).replace("{user}", user?.name || ""));
    api.get(`/orders/${order.id}/recipients`).then((r) => {
      setContacts(r.data);
      if (r.data.length) { setSelected(r.data[0].id); setTo(r.data[0].email); }
      else { setSelected("custom"); setTo(""); }
    }).catch(() => setContacts([]));
  }, [open, order?.id]);

  const pickContact = (val) => {
    setSelected(val);
    if (val === "custom") { setTo(""); return; }
    const c = contacts.find((x) => x.id === val);
    if (c) setTo(c.email);
  };

  const preview = async () => {
    try {
      const token = localStorage.getItem("lf_token");
      const res = await fetch(`${API}/orders/${order.id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("PDF error"); }
  };

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/orders/${order.id}/send-confirmation`, { to, subject, message });
      toast.success(r.data.sent ? t("email_sent_ok") : t("email_simulated"));
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4" />{t("ord_send_ab")}</DialogTitle>
          <DialogDescription>{order?.order_number} · {order?.account_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">{t("recipient")} *</Label>
            <select data-testid="oc-recipient-select" className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
              value={selected} onChange={(e) => pickContact(e.target.value)}>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>{`${c.first_name} ${c.last_name} <${c.email}>`}</option>
              ))}
              <option value="custom">{t("ord_custom_recipient")}</option>
            </select>
            {selected === "custom" && (
              <Input data-testid="oc-to-input" type="email" placeholder="kunde@firma.de" className="mt-1"
                value={to} onChange={(e) => setTo(e.target.value)} />
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">{t("subject")}</Label>
            <Input data-testid="oc-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">{t("message")}</Label>
            <Textarea data-testid="oc-message-input" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={preview} data-testid="oc-preview-btn">
              <FileDown className="h-3.5 w-3.5 mr-1" />{t("ord_ab_preview")}
            </Button>
            <Button className="flex-1 bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" disabled={!to.includes("@") || sending}
              onClick={send} data-testid="oc-send-btn">
              {sending ? "…" : t("send")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
